import React, { useMemo, useState, useEffect } from 'react';
import { useT } from '../context/LanguageContext';

/**
 * Telefoonnummer input met landcode dropdown.
 *
 * Opslag-formaat (E.164-achtig): "+31612345678" — landcode + nummer aan elkaar,
 * geen streepjes/spaties. Voor weergave gebruikt <PhoneDisplay /> om e.g. "+31 6 12345678" te tonen.
 *
 * Usage:
 *   <PhoneInput value={klant.telefoon} onChange={(v) => setKlant({ ...klant, telefoon: v })} />
 */

// Veelvoorkomende landen bovenaan (NL → BE → DE → FR → GB), daarna alle overige landen
// alfabetisch op Nederlandse naam. Volledige ITU-T E.164 dial codes.
export const COUNTRY_CODES = [
  { code: 'NL', dial: '+31',  name: 'Nederland',           flag: '🇳🇱' },
  { code: 'BE', dial: '+32',  name: 'België',              flag: '🇧🇪' },
  { code: 'DE', dial: '+49',  name: 'Duitsland',           flag: '🇩🇪' },
  { code: 'FR', dial: '+33',  name: 'Frankrijk',           flag: '🇫🇷' },
  { code: 'GB', dial: '+44',  name: 'Verenigd Koninkrijk', flag: '🇬🇧' },
  // -- alfabetisch (NL naam) --
  { code: 'AF', dial: '+93',  name: 'Afghanistan',         flag: '🇦🇫' },
  { code: 'AL', dial: '+355', name: 'Albanië',             flag: '🇦🇱' },
  { code: 'DZ', dial: '+213', name: 'Algerije',            flag: '🇩🇿' },
  { code: 'AD', dial: '+376', name: 'Andorra',             flag: '🇦🇩' },
  { code: 'AO', dial: '+244', name: 'Angola',              flag: '🇦🇴' },
  { code: 'AR', dial: '+54',  name: 'Argentinië',          flag: '🇦🇷' },
  { code: 'AM', dial: '+374', name: 'Armenië',             flag: '🇦🇲' },
  { code: 'AW', dial: '+297', name: 'Aruba',               flag: '🇦🇼' },
  { code: 'AU', dial: '+61',  name: 'Australië',           flag: '🇦🇺' },
  { code: 'AZ', dial: '+994', name: 'Azerbeidzjan',        flag: '🇦🇿' },
  { code: 'BS', dial: '+1242', name: 'Bahama\'s',          flag: '🇧🇸' },
  { code: 'BH', dial: '+973', name: 'Bahrein',             flag: '🇧🇭' },
  { code: 'BD', dial: '+880', name: 'Bangladesh',          flag: '🇧🇩' },
  { code: 'BB', dial: '+1246', name: 'Barbados',           flag: '🇧🇧' },
  { code: 'BY', dial: '+375', name: 'Belarus',             flag: '🇧🇾' },
  { code: 'BZ', dial: '+501', name: 'Belize',              flag: '🇧🇿' },
  { code: 'BJ', dial: '+229', name: 'Benin',               flag: '🇧🇯' },
  { code: 'BT', dial: '+975', name: 'Bhutan',              flag: '🇧🇹' },
  { code: 'BO', dial: '+591', name: 'Bolivia',             flag: '🇧🇴' },
  { code: 'BA', dial: '+387', name: 'Bosnië en Herzegovina', flag: '🇧🇦' },
  { code: 'BW', dial: '+267', name: 'Botswana',            flag: '🇧🇼' },
  { code: 'BR', dial: '+55',  name: 'Brazilië',            flag: '🇧🇷' },
  { code: 'BN', dial: '+673', name: 'Brunei',              flag: '🇧🇳' },
  { code: 'BG', dial: '+359', name: 'Bulgarije',           flag: '🇧🇬' },
  { code: 'BF', dial: '+226', name: 'Burkina Faso',        flag: '🇧🇫' },
  { code: 'BI', dial: '+257', name: 'Burundi',             flag: '🇧🇮' },
  { code: 'KH', dial: '+855', name: 'Cambodja',            flag: '🇰🇭' },
  { code: 'CA', dial: '+1',   name: 'Canada',              flag: '🇨🇦' },
  { code: 'CF', dial: '+236', name: 'Centraal-Afrikaanse Republiek', flag: '🇨🇫' },
  { code: 'CL', dial: '+56',  name: 'Chili',               flag: '🇨🇱' },
  { code: 'CN', dial: '+86',  name: 'China',               flag: '🇨🇳' },
  { code: 'CO', dial: '+57',  name: 'Colombia',            flag: '🇨🇴' },
  { code: 'KM', dial: '+269', name: 'Comoren',             flag: '🇰🇲' },
  { code: 'CG', dial: '+242', name: 'Congo-Brazzaville',   flag: '🇨🇬' },
  { code: 'CD', dial: '+243', name: 'Congo-Kinshasa',      flag: '🇨🇩' },
  { code: 'CR', dial: '+506', name: 'Costa Rica',          flag: '🇨🇷' },
  { code: 'CU', dial: '+53',  name: 'Cuba',                flag: '🇨🇺' },
  { code: 'CW', dial: '+599', name: 'Curaçao',             flag: '🇨🇼' },
  { code: 'CY', dial: '+357', name: 'Cyprus',              flag: '🇨🇾' },
  { code: 'DK', dial: '+45',  name: 'Denemarken',          flag: '🇩🇰' },
  { code: 'DJ', dial: '+253', name: 'Djibouti',            flag: '🇩🇯' },
  { code: 'DM', dial: '+1767', name: 'Dominica',           flag: '🇩🇲' },
  { code: 'DO', dial: '+1809', name: 'Dominicaanse Republiek', flag: '🇩🇴' },
  { code: 'EC', dial: '+593', name: 'Ecuador',             flag: '🇪🇨' },
  { code: 'EG', dial: '+20',  name: 'Egypte',              flag: '🇪🇬' },
  { code: 'SV', dial: '+503', name: 'El Salvador',         flag: '🇸🇻' },
  { code: 'GQ', dial: '+240', name: 'Equatoriaal-Guinea',  flag: '🇬🇶' },
  { code: 'ER', dial: '+291', name: 'Eritrea',             flag: '🇪🇷' },
  { code: 'EE', dial: '+372', name: 'Estland',             flag: '🇪🇪' },
  { code: 'ET', dial: '+251', name: 'Ethiopië',            flag: '🇪🇹' },
  { code: 'FO', dial: '+298', name: 'Faeröer',             flag: '🇫🇴' },
  { code: 'FJ', dial: '+679', name: 'Fiji',                flag: '🇫🇯' },
  { code: 'PH', dial: '+63',  name: 'Filipijnen',          flag: '🇵🇭' },
  { code: 'FI', dial: '+358', name: 'Finland',             flag: '🇫🇮' },
  { code: 'GA', dial: '+241', name: 'Gabon',               flag: '🇬🇦' },
  { code: 'GM', dial: '+220', name: 'Gambia',              flag: '🇬🇲' },
  { code: 'GE', dial: '+995', name: 'Georgië',             flag: '🇬🇪' },
  { code: 'GH', dial: '+233', name: 'Ghana',               flag: '🇬🇭' },
  { code: 'GI', dial: '+350', name: 'Gibraltar',           flag: '🇬🇮' },
  { code: 'GD', dial: '+1473', name: 'Grenada',            flag: '🇬🇩' },
  { code: 'GR', dial: '+30',  name: 'Griekenland',         flag: '🇬🇷' },
  { code: 'GL', dial: '+299', name: 'Groenland',           flag: '🇬🇱' },
  { code: 'GT', dial: '+502', name: 'Guatemala',           flag: '🇬🇹' },
  { code: 'GN', dial: '+224', name: 'Guinee',              flag: '🇬🇳' },
  { code: 'GW', dial: '+245', name: 'Guinee-Bissau',       flag: '🇬🇼' },
  { code: 'GY', dial: '+592', name: 'Guyana',              flag: '🇬🇾' },
  { code: 'HT', dial: '+509', name: 'Haïti',               flag: '🇭🇹' },
  { code: 'HN', dial: '+504', name: 'Honduras',            flag: '🇭🇳' },
  { code: 'HU', dial: '+36',  name: 'Hongarije',           flag: '🇭🇺' },
  { code: 'HK', dial: '+852', name: 'Hongkong',            flag: '🇭🇰' },
  { code: 'IE', dial: '+353', name: 'Ierland',             flag: '🇮🇪' },
  { code: 'IS', dial: '+354', name: 'IJsland',             flag: '🇮🇸' },
  { code: 'IN', dial: '+91',  name: 'India',               flag: '🇮🇳' },
  { code: 'ID', dial: '+62',  name: 'Indonesië',           flag: '🇮🇩' },
  { code: 'IQ', dial: '+964', name: 'Irak',                flag: '🇮🇶' },
  { code: 'IR', dial: '+98',  name: 'Iran',                flag: '🇮🇷' },
  { code: 'IL', dial: '+972', name: 'Israël',              flag: '🇮🇱' },
  { code: 'IT', dial: '+39',  name: 'Italië',              flag: '🇮🇹' },
  { code: 'CI', dial: '+225', name: 'Ivoorkust',           flag: '🇨🇮' },
  { code: 'JM', dial: '+1876', name: 'Jamaica',            flag: '🇯🇲' },
  { code: 'JP', dial: '+81',  name: 'Japan',               flag: '🇯🇵' },
  { code: 'YE', dial: '+967', name: 'Jemen',               flag: '🇾🇪' },
  { code: 'JO', dial: '+962', name: 'Jordanië',            flag: '🇯🇴' },
  { code: 'CV', dial: '+238', name: 'Kaapverdië',          flag: '🇨🇻' },
  { code: 'CM', dial: '+237', name: 'Kameroen',            flag: '🇨🇲' },
  { code: 'KZ', dial: '+7',   name: 'Kazachstan',          flag: '🇰🇿' },
  { code: 'KE', dial: '+254', name: 'Kenia',               flag: '🇰🇪' },
  { code: 'KG', dial: '+996', name: 'Kirgizië',            flag: '🇰🇬' },
  { code: 'KI', dial: '+686', name: 'Kiribati',            flag: '🇰🇮' },
  { code: 'KW', dial: '+965', name: 'Koeweit',             flag: '🇰🇼' },
  { code: 'XK', dial: '+383', name: 'Kosovo',              flag: '🇽🇰' },
  { code: 'HR', dial: '+385', name: 'Kroatië',             flag: '🇭🇷' },
  { code: 'LA', dial: '+856', name: 'Laos',                flag: '🇱🇦' },
  { code: 'LS', dial: '+266', name: 'Lesotho',             flag: '🇱🇸' },
  { code: 'LV', dial: '+371', name: 'Letland',             flag: '🇱🇻' },
  { code: 'LB', dial: '+961', name: 'Libanon',             flag: '🇱🇧' },
  { code: 'LR', dial: '+231', name: 'Liberia',             flag: '🇱🇷' },
  { code: 'LY', dial: '+218', name: 'Libië',               flag: '🇱🇾' },
  { code: 'LI', dial: '+423', name: 'Liechtenstein',       flag: '🇱🇮' },
  { code: 'LT', dial: '+370', name: 'Litouwen',            flag: '🇱🇹' },
  { code: 'LU', dial: '+352', name: 'Luxemburg',           flag: '🇱🇺' },
  { code: 'MO', dial: '+853', name: 'Macau',               flag: '🇲🇴' },
  { code: 'MK', dial: '+389', name: 'Noord-Macedonië',     flag: '🇲🇰' },
  { code: 'MG', dial: '+261', name: 'Madagaskar',          flag: '🇲🇬' },
  { code: 'MW', dial: '+265', name: 'Malawi',              flag: '🇲🇼' },
  { code: 'MV', dial: '+960', name: 'Malediven',           flag: '🇲🇻' },
  { code: 'MY', dial: '+60',  name: 'Maleisië',            flag: '🇲🇾' },
  { code: 'ML', dial: '+223', name: 'Mali',                flag: '🇲🇱' },
  { code: 'MT', dial: '+356', name: 'Malta',               flag: '🇲🇹' },
  { code: 'MA', dial: '+212', name: 'Marokko',             flag: '🇲🇦' },
  { code: 'MR', dial: '+222', name: 'Mauritanië',          flag: '🇲🇷' },
  { code: 'MU', dial: '+230', name: 'Mauritius',           flag: '🇲🇺' },
  { code: 'MX', dial: '+52',  name: 'Mexico',              flag: '🇲🇽' },
  { code: 'MD', dial: '+373', name: 'Moldavië',            flag: '🇲🇩' },
  { code: 'MC', dial: '+377', name: 'Monaco',              flag: '🇲🇨' },
  { code: 'MN', dial: '+976', name: 'Mongolië',            flag: '🇲🇳' },
  { code: 'ME', dial: '+382', name: 'Montenegro',          flag: '🇲🇪' },
  { code: 'MZ', dial: '+258', name: 'Mozambique',          flag: '🇲🇿' },
  { code: 'MM', dial: '+95',  name: 'Myanmar',             flag: '🇲🇲' },
  { code: 'NA', dial: '+264', name: 'Namibië',             flag: '🇳🇦' },
  { code: 'NP', dial: '+977', name: 'Nepal',               flag: '🇳🇵' },
  { code: 'NI', dial: '+505', name: 'Nicaragua',           flag: '🇳🇮' },
  { code: 'NZ', dial: '+64',  name: 'Nieuw-Zeeland',       flag: '🇳🇿' },
  { code: 'NE', dial: '+227', name: 'Niger',               flag: '🇳🇪' },
  { code: 'NG', dial: '+234', name: 'Nigeria',             flag: '🇳🇬' },
  { code: 'KP', dial: '+850', name: 'Noord-Korea',         flag: '🇰🇵' },
  { code: 'NO', dial: '+47',  name: 'Noorwegen',           flag: '🇳🇴' },
  { code: 'UG', dial: '+256', name: 'Oeganda',             flag: '🇺🇬' },
  { code: 'UA', dial: '+380', name: 'Oekraïne',            flag: '🇺🇦' },
  { code: 'UZ', dial: '+998', name: 'Oezbekistan',         flag: '🇺🇿' },
  { code: 'OM', dial: '+968', name: 'Oman',                flag: '🇴🇲' },
  { code: 'TL', dial: '+670', name: 'Oost-Timor',          flag: '🇹🇱' },
  { code: 'AT', dial: '+43',  name: 'Oostenrijk',          flag: '🇦🇹' },
  { code: 'PK', dial: '+92',  name: 'Pakistan',            flag: '🇵🇰' },
  { code: 'PW', dial: '+680', name: 'Palau',               flag: '🇵🇼' },
  { code: 'PS', dial: '+970', name: 'Palestina',           flag: '🇵🇸' },
  { code: 'PA', dial: '+507', name: 'Panama',              flag: '🇵🇦' },
  { code: 'PG', dial: '+675', name: 'Papoea-Nieuw-Guinea', flag: '🇵🇬' },
  { code: 'PY', dial: '+595', name: 'Paraguay',            flag: '🇵🇾' },
  { code: 'PE', dial: '+51',  name: 'Peru',                flag: '🇵🇪' },
  { code: 'PL', dial: '+48',  name: 'Polen',               flag: '🇵🇱' },
  { code: 'PT', dial: '+351', name: 'Portugal',            flag: '🇵🇹' },
  { code: 'PR', dial: '+1787', name: 'Puerto Rico',        flag: '🇵🇷' },
  { code: 'QA', dial: '+974', name: 'Qatar',               flag: '🇶🇦' },
  { code: 'RO', dial: '+40',  name: 'Roemenië',            flag: '🇷🇴' },
  { code: 'RU', dial: '+7',   name: 'Rusland',             flag: '🇷🇺' },
  { code: 'RW', dial: '+250', name: 'Rwanda',              flag: '🇷🇼' },
  { code: 'KN', dial: '+1869', name: 'Saint Kitts en Nevis', flag: '🇰🇳' },
  { code: 'LC', dial: '+1758', name: 'Saint Lucia',        flag: '🇱🇨' },
  { code: 'VC', dial: '+1784', name: 'Saint Vincent',      flag: '🇻🇨' },
  { code: 'SB', dial: '+677', name: 'Salomonseilanden',    flag: '🇸🇧' },
  { code: 'WS', dial: '+685', name: 'Samoa',               flag: '🇼🇸' },
  { code: 'SM', dial: '+378', name: 'San Marino',          flag: '🇸🇲' },
  { code: 'ST', dial: '+239', name: 'Sao Tomé en Principe', flag: '🇸🇹' },
  { code: 'SA', dial: '+966', name: 'Saoedi-Arabië',       flag: '🇸🇦' },
  { code: 'SN', dial: '+221', name: 'Senegal',             flag: '🇸🇳' },
  { code: 'RS', dial: '+381', name: 'Servië',              flag: '🇷🇸' },
  { code: 'SC', dial: '+248', name: 'Seychellen',          flag: '🇸🇨' },
  { code: 'SL', dial: '+232', name: 'Sierra Leone',        flag: '🇸🇱' },
  { code: 'SG', dial: '+65',  name: 'Singapore',           flag: '🇸🇬' },
  { code: 'SX', dial: '+1721', name: 'Sint Maarten',       flag: '🇸🇽' },
  { code: 'SI', dial: '+386', name: 'Slovenië',            flag: '🇸🇮' },
  { code: 'SK', dial: '+421', name: 'Slowakije',           flag: '🇸🇰' },
  { code: 'SD', dial: '+249', name: 'Soedan',              flag: '🇸🇩' },
  { code: 'SO', dial: '+252', name: 'Somalië',             flag: '🇸🇴' },
  { code: 'ES', dial: '+34',  name: 'Spanje',              flag: '🇪🇸' },
  { code: 'LK', dial: '+94',  name: 'Sri Lanka',           flag: '🇱🇰' },
  { code: 'SR', dial: '+597', name: 'Suriname',            flag: '🇸🇷' },
  { code: 'SZ', dial: '+268', name: 'Swaziland',           flag: '🇸🇿' },
  { code: 'SY', dial: '+963', name: 'Syrië',               flag: '🇸🇾' },
  { code: 'TJ', dial: '+992', name: 'Tadzjikistan',        flag: '🇹🇯' },
  { code: 'TW', dial: '+886', name: 'Taiwan',              flag: '🇹🇼' },
  { code: 'TZ', dial: '+255', name: 'Tanzania',            flag: '🇹🇿' },
  { code: 'TH', dial: '+66',  name: 'Thailand',            flag: '🇹🇭' },
  { code: 'TG', dial: '+228', name: 'Togo',                flag: '🇹🇬' },
  { code: 'TO', dial: '+676', name: 'Tonga',               flag: '🇹🇴' },
  { code: 'TT', dial: '+1868', name: 'Trinidad en Tobago', flag: '🇹🇹' },
  { code: 'TD', dial: '+235', name: 'Tsjaad',              flag: '🇹🇩' },
  { code: 'CZ', dial: '+420', name: 'Tsjechië',            flag: '🇨🇿' },
  { code: 'TN', dial: '+216', name: 'Tunesië',             flag: '🇹🇳' },
  { code: 'TR', dial: '+90',  name: 'Turkije',             flag: '🇹🇷' },
  { code: 'TM', dial: '+993', name: 'Turkmenistan',        flag: '🇹🇲' },
  { code: 'UY', dial: '+598', name: 'Uruguay',             flag: '🇺🇾' },
  { code: 'VU', dial: '+678', name: 'Vanuatu',             flag: '🇻🇺' },
  { code: 'VA', dial: '+379', name: 'Vaticaanstad',        flag: '🇻🇦' },
  { code: 'VE', dial: '+58',  name: 'Venezuela',           flag: '🇻🇪' },
  { code: 'AE', dial: '+971', name: 'Verenigde Arabische Emiraten', flag: '🇦🇪' },
  { code: 'US', dial: '+1',   name: 'Verenigde Staten',    flag: '🇺🇸' },
  { code: 'VN', dial: '+84',  name: 'Vietnam',             flag: '🇻🇳' },
  { code: 'ZM', dial: '+260', name: 'Zambia',              flag: '🇿🇲' },
  { code: 'ZW', dial: '+263', name: 'Zimbabwe',            flag: '🇿🇼' },
  { code: 'ZA', dial: '+27',  name: 'Zuid-Afrika',         flag: '🇿🇦' },
  { code: 'KR', dial: '+82',  name: 'Zuid-Korea',          flag: '🇰🇷' },
  { code: 'SS', dial: '+211', name: 'Zuid-Soedan',         flag: '🇸🇸' },
  { code: 'SE', dial: '+46',  name: 'Zweden',              flag: '🇸🇪' },
  { code: 'CH', dial: '+41',  name: 'Zwitserland',         flag: '🇨🇭' },
];

const DEFAULT_DIAL = '+31';

// Sorteer dial-codes lang→kort zodat "+352" matcht vóór "+3"
const DIALS_SORTED = [...COUNTRY_CODES].sort((a, b) => b.dial.length - a.dial.length);

// Parse opgeslagen waarde naar { dial, national }
export function parsePhone(value) {
  const s = String(value || '').replace(/[\s\-().]/g, '');
  if (s.startsWith('00')) {
    // 0049... -> +49...
    const rest = s.slice(2);
    for (const c of DIALS_SORTED) {
      const cd = c.dial.slice(1);
      if (rest.startsWith(cd)) return { dial: c.dial, national: rest.slice(cd.length) };
    }
  }
  if (s.startsWith('+')) {
    for (const c of DIALS_SORTED) {
      if (s.startsWith(c.dial)) return { dial: c.dial, national: s.slice(c.dial.length) };
    }
    return { dial: DEFAULT_DIAL, national: s.replace(/^\+/, '') };
  }
  // Geen prefix: behandel als lokaal NL nummer, strip leidende 0
  return { dial: DEFAULT_DIAL, national: s.replace(/^0+/, '') };
}

export function formatPhoneDisplay(value) {
  if (!value) return '';
  const { dial, national } = parsePhone(value);
  if (!national) return dial;
  // Lichte groepering: NL mobiel: "+31 6 12345678"
  if (dial === '+31' && national.startsWith('6') && national.length === 9) {
    return `${dial} 6 ${national.slice(1, 5)}${national.slice(5)}`;
  }
  return `${dial} ${national}`;
}

export default function PhoneInput({ value, onChange, placeholder, testid }) {
  const { t } = useT();
  const parsed = useMemo(() => parsePhone(value), [value]);
  // Local state voor wat de gebruiker tikt — pas op blur normaliseren we naar value.
  const [localText, setLocalText] = useState(parsed.national);
  // Sync wanneer parent value verandert (bv. via address lookup of save+reopen)
  useEffect(() => { setLocalText(parsed.national); }, [parsed.national]);

  const setDial = (d) => onChange(`${d}${parsed.national}`);

  const handleBlur = () => {
    const raw = String(localText || '').replace(/[\s\-().]/g, '');
    if (!raw) { onChange(parsed.dial); return; }
    let detectedDial = null;
    let rest = raw;
    if (raw.startsWith('+')) {
      for (const c of DIALS_SORTED) {
        if (raw.startsWith(c.dial)) { detectedDial = c.dial; rest = raw.slice(c.dial.length); break; }
      }
    } else if (raw.startsWith('00')) {
      const r = raw.slice(2);
      for (const c of DIALS_SORTED) {
        const cd = c.dial.slice(1);
        if (r.startsWith(cd)) { detectedDial = c.dial; rest = r.slice(cd.length); break; }
      }
    }
    if (detectedDial) {
      const clean = rest.replace(/^0+/, '').replace(/[^\d]/g, '');
      onChange(`${detectedDial}${clean}`);
      setLocalText(clean);
    } else if (raw.startsWith('+') || raw.startsWith('00')) {
      onChange(raw);
      setLocalText(raw);
    } else {
      const local = raw.replace(/^0+/, '').replace(/[^\d]/g, '');
      onChange(`${parsed.dial}${local}`);
      setLocalText(local);
    }
  };

  return (
    <div style={{ display: 'flex', gap: 6 }} data-testid={testid || 'phone-input'}>
      <select
        className="cs-input"
        style={{ width: 180, flex: '0 0 180px', padding: '6px 4px' }}
        value={parsed.dial}
        onChange={(e) => setDial(e.target.value)}
        data-testid="phone-dial"
        title={t('Landcode (wordt automatisch herkend bij +31, 0049, etc.)')}
      >
        {COUNTRY_CODES.map(c => (
          <option key={c.code} value={c.dial}>{c.flag} {c.dial} · {c.name}</option>
        ))}
      </select>
      <input
        className="cs-input"
        style={{ flex: 1 }}
        value={localText}
        onChange={(e) => setLocalText(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || '612345678 / +49 1701234567'}
        inputMode="tel"
        data-testid="phone-national"
        title={t('Typ +31, 0049, +44, of 06… — landcode wordt automatisch herkend na uit-tikken')}
      />
    </div>
  );
}
